import 'dayjs/locale/sv'; // Import Swedish locale

import { CheckOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Col, Row, Typography } from 'antd';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import React from 'react';
import styled from 'styled-components';

const { Title, Text, Paragraph } = Typography;

dayjs.extend(advancedFormat);

const PlanCard = styled(Card)<{ selected?: boolean }>`
  border: ${props =>
    props.selected ? '2px solid #1890ff' : '1px solid #d9d9d9'};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: #1890ff;
    box-shadow: 0 2px 8px rgba(24, 144, 255, 0.15);
  }
`;

const PriceText = styled(Title)`
  color: #1890ff !important;
  margin: 0 !important;
`;

const FeatureList = styled.ul`
  margin: 16px 0;
  padding-left: 20px;
  text-align: left;
`;

interface Props {
  paywallStatus: any;
  onPlanSelect: (plan: string) => void;
  isTrialExpired: boolean;
  daysLeft: number;
}

function PlanSelection({
  paywallStatus,
  onPlanSelect,
  isTrialExpired,
  daysLeft,
}: Props): React.ReactElement {
  const { priceOne, priceTwo, commissionPercentage } = paywallStatus;

  // Calculate the trial end date
  const trialEndDate = dayjs().add(daysLeft, 'days').locale('sv');
  const formattedDate = trialEndDate
    .format('Do MMMM')
    // add : between the day and the ordinal
    .replace(/(\d+)([a-zA-Z]+)/, '$1:$2');

  return (
    <div>
      {!isTrialExpired && daysLeft > 0 && (
        <Alert
          message={`Välj det paket nedan som passar er bäst med start ${formattedDate}`}
          //description="Välj ett abonnemang för att fortsätta använda Portally efter testperioden."
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}

      {isTrialExpired && (
        <Alert
          message="Välj ett abonnemang för att fortsätta använda Portally"
          // description="Du måste välja ett abonnemang för att fortsätta använda Portally."
          type="warning"
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}

      <Title level={3} style={{ textAlign: 'center', marginBottom: 32 }}>
        Välj ditt abonnemang
      </Title>

      <Row gutter={24} justify="center">
        <Col xs={24} sm={12} md={10}>
          <PlanCard>
            <div style={{ textAlign: 'center' }}>
              <Title level={4}>Fast pris</Title>
              <PriceText level={2}>
                {priceOne?.toLocaleString('sv-SE')} kr
              </PriceText>
              <Text type="secondary">per månad</Text>

              <FeatureList>
                <li>Inget förmedlingsarvode</li>
                <li>Antal annonser: Obegränsat</li>
                <li>Antal användare: Obegränsat</li>
                <li>Antal leads: Obegränsat</li>
                <li>Antal län: Obegränsat</li>
              </FeatureList>

              <Button
                size="large"
                block
                onClick={() => onPlanSelect('fixed')}
                icon={<CheckOutlined />}
                type="primary"
              >
                Välj fast pris
              </Button>
            </div>
          </PlanCard>
        </Col>

        <Col xs={24} sm={12} md={10}>
          <PlanCard>
            <div style={{ textAlign: 'center' }}>
              <Title level={4}>Mix</Title>
              <PriceText level={2}>
                {priceTwo?.toLocaleString('sv-SE')} kr
              </PriceText>
              <Text type="secondary">per månad</Text>

              <FeatureList>
                <li>Förmedlingsarvode: {commissionPercentage}%</li>
                <li>Antal annonser: Obegränsat</li>
                <li>Antal användare: Obegränsat</li>
                <li>Antal leads: Obegränsat</li>
                <li>Antal län: Obegränsat</li>
              </FeatureList>

              <Button
                type="primary"
                size="large"
                block
                onClick={() => onPlanSelect('mix')}
                icon={<CheckOutlined />}
              >
                Välj mix
              </Button>
            </div>
          </PlanCard>
        </Col>
      </Row>

      <div style={{ textAlign: 'center', marginTop: 32 }}>
        <Paragraph type="secondary">
          Har du frågor? Kontakta oss på{' '}
          <a href="mailto:support@portally.com">support@portally.com</a>
        </Paragraph>
      </div>
    </div>
  );
}

export default PlanSelection;
